/**
 * useRouteBasedPrefetch
 *
 * Precarica in background i chunk JavaScript delle pagine correlate a quella corrente,
 * dopo che la pagina è completamente caricata e stabile (post-hydration + 1500ms).
 *
 * Strategia:
 * - Attende waitForHydrationComplete() + 1500ms prima di partire
 * - Usa requestIdleCallback per non bloccare mai il main thread
 * - Skip su mobile (< 1024px) per non consumare dati mobili
 * - Guard hasStarted per evitare doppio prefetch su re-render
 * - Ogni import() è silenzioso (.catch(() => {}))
 *
 * Il trick: chiamare lo stesso import() usato da React.lazy popola la cache
 * del browser. Quando React.lazy tenterà di caricare il chunk, lo trova già
 * in cache HTTP → zero latenza, nessun PageLoader spinner.
 */

import { useEffect, useRef } from 'react';
import { waitForHydrationComplete } from '@/utils/hydrationManager';
import { debugLog } from '@/utils/debugLogger';

// Mappa rotta → lista ordinata di import dinamici da prefetchare
// Ogni entry è una factory che restituisce un import() dinamico
// L'ordine rispecchia la probabilità di navigazione dalla rotta corrente
const ROUTE_PREFETCH_MAP: Record<string, Array<() => Promise<unknown>>> = {
  '/': [
    () => import('@/pages/Dealers'),
    () => import('@/pages/Blog'),
    () => import('@/pages/ServerPredictionsPage'),
  ],
  '/previsioni': [
    () => import('@/pages/Dealers'),
    () => import('@/pages/Charts'),
    () => import('@/pages/Blog'),
  ],
  '/compro-oro': [
    () => import('@/pages/DealersByCity'),
    () => import('@/pages/ServerPredictionsPage'),
    () => import('@/pages/Charts'),
  ],
  '/compro-oro/roma': [
    () => import('@/pages/ComproOroRomaTuscolana'),
    () => import('@/pages/ComproOroRomaSanGiovanni'),
    () => import('@/pages/DealerDetail'),
    () => import('@/pages/ServerPredictionsPage'),
  ],
  '/blog': [
    () => import('@/pages/BlogPost'),
    () => import('@/pages/Dealers'),
    () => import('@/pages/ServerPredictionsPage'),
  ],
  '/grafici': [
    () => import('@/pages/ServerPredictionsPage'),
    () => import('@/pages/Dealers'),
    () => import('@/pages/Blog'),
  ],
  '/faq': [
    () => import('@/pages/Dealers'),
    () => import('@/pages/Blog'),
    () => import('@/pages/Charts'),
  ],
  '/assistente-ai': [
    () => import('@/pages/Dealers'),
    () => import('@/pages/Charts'),
    () => import('@/pages/ServerPredictionsPage'),
  ],
  '/oro-investimento': [
    () => import('@/pages/MoneteOroInvestimento'),
    () => import('@/pages/ServerLingottiOroInvestimentoPage'),
    () => import('@/pages/CoinDetailPage'),
  ],
  '/diamanti': [
    () => import('@/pages/QuotazioneDiamanti'),
    () => import('@/pages/Le4CDelDiamante'),
    () => import('@/pages/CalcolatoreCaratiDiamante'),
  ],
};

/**
 * Resolve the prefetch list for a given pathname.
 * Supports exact match and prefix match (e.g. /compro-oro/roma/... → /compro-oro/roma).
 */
function resolvePrefetchList(pathname: string): Array<() => Promise<unknown>> {
  // Exact match first
  if (ROUTE_PREFETCH_MAP[pathname]) {
    return ROUTE_PREFETCH_MAP[pathname];
  }

  // Try prefix match (longest prefix wins)
  const sortedKeys = Object.keys(ROUTE_PREFETCH_MAP).sort((a, b) => b.length - a.length);
  for (const key of sortedKeys) {
    if (key !== '/' && pathname.startsWith(key)) {
      return ROUTE_PREFETCH_MAP[key];
    }
  }

  return [];
}

/**
 * Schedules a single import() during idle time.
 * Resolves when the import completes (or fails silently).
 */
function idleImport(importFn: () => Promise<unknown>, label: string): Promise<void> {
  return new Promise<void>((resolve) => {
    const run = async () => {
      try {
        await importFn();
        debugLog('general', `[RouteBasedPrefetch] Prefetched: ${label}`, undefined, 'RouteBasedPrefetch');
      } catch {
        // Silent failure: prefetch is best-effort
      }
      resolve();
    };

    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(run, { timeout: 5000 });
    } else {
      setTimeout(run, 500);
    }
  });
}

/**
 * Hook that triggers background prefetching of related page chunks
 * after the current page is fully loaded and stable.
 *
 * @param pathname - Current route pathname (from useLocation)
 * @param isHydrated - Flag from AppInner indicating hydration is complete
 */
export function useRouteBasedPrefetch(pathname: string, isHydrated: boolean): void {
  const hasStarted = useRef(false);

  useEffect(() => {
    // Only run after hydration is flagged complete
    if (!isHydrated) return;

    // Guard against double execution (StrictMode, re-renders)
    if (hasStarted.current) return;
    hasStarted.current = true;

    // Skip on mobile to preserve data
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches) {
      debugLog('general', '[RouteBasedPrefetch] Skipped on mobile', undefined, 'RouteBasedPrefetch');
      return;
    }

    const modulesToPrefetch = resolvePrefetchList(pathname);
    if (modulesToPrefetch.length === 0) {
      debugLog('general', `[RouteBasedPrefetch] No prefetch map for: ${pathname}`, undefined, 'RouteBasedPrefetch');
      return;
    }

    debugLog('general', `[RouteBasedPrefetch] Scheduling ${modulesToPrefetch.length} prefetches for: ${pathname}`, undefined, 'RouteBasedPrefetch');

    // Wait for hydration complete signal + 1500ms safety margin before starting prefetch
    let cancelled = false;

    const startPrefetch = async () => {
      // Extra 1500ms after hydration to ensure page is stable and interactive
      await new Promise<void>((r) => setTimeout(r, 1500));
      if (cancelled) return;

      // Sequential prefetch during idle time (one per idle slot)
      for (let i = 0; i < modulesToPrefetch.length; i++) {
        if (cancelled) break;
        await idleImport(modulesToPrefetch[i], `priority-${i + 1} for ${pathname}`);
      }
    };

    // Ensure we're past hydration gate before scheduling
    waitForHydrationComplete().then(() => {
      if (!cancelled) {
        startPrefetch();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isHydrated, pathname]);
}
